import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X, UsersThree, ArrowRight, Users, Key, Trophy } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useActiveGroup } from "../contexts/ActiveGroupContext";

export default function Groups() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { refresh: refreshActiveGroups, setActiveGroupId } = useActiveGroup();
  const [groups, setGroups] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [mode, setMode] = useState(null);
  const [form, setForm] = useState({ name: "", message: "", code: "", tournament_id: "", scoring_system: "classic" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const load = useCallback(async () => {
    setInitialLoading(true);
    const [gs, ts] = await Promise.all([
      refreshActiveGroups(),
      api.get("/tournaments", { params: { selectable_for_groups: true } }),
    ]);
    setGroups(gs);
    setTournaments(ts.data);
    const popular = ts.data.find((x) => x.popular) || ts.data[0];
    if (popular) {
      setForm((f) => (f.tournament_id ? f : { ...f, tournament_id: popular.tournament_id }));
    }
    setInitialLoading(false);
  }, [refreshActiveGroups]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/groups", {
        name: form.name,
        message: form.message,
        tournament_id: form.tournament_id,
        scoring_system: form.scoring_system,
      });
      setMode(null); setForm({ ...form, name: "", message: "", code: "", scoring_system: "classic" });
      await load();
      setActiveGroupId(data.group_id);
      navigate(`/app/groups/${data.group_id}`);
    } catch (e) {
      setError(e?.response?.data?.detail || t("could_not_create_group"));
    } finally { setLoading(false); }
  };

  const join = async () => {
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/groups/join", { invite_code: form.code.trim() });
      setMode(null); setForm({ ...form, name: "", message: "", code: "" });
      await load();
      setActiveGroupId(data.group_id);
      navigate(`/app/groups/${data.group_id}`);
    } catch (e) {
      setError(e?.response?.data?.detail || t("could_not_join"));
    } finally { setLoading(false); }
  };

  return (
    <div className="px-4 pt-4" data-testid="groups-page">
      <div className="mb-5">
        <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-neutral-400">{t("invite_friends")}</div>
        <h1 className="font-display text-3xl text-[#02371b] leading-none mt-1">{t("your_groups")}</h1>
      </div>

      {!mode && (
        <div className="grid grid-cols-2 gap-3 mb-6">
          <button type="button" data-testid="btn-create-group" onClick={() => setMode("create")}
            className="bg-[#02371b] text-white rounded-2xl p-4 flex flex-col items-start gap-3 active:scale-[0.98] transition-transform">
            <div className="w-10 h-10 rounded-full bg-[#41a42b] flex items-center justify-center"><Plus size={20} weight="bold" /></div>
            <div className="font-display text-sm leading-tight text-left">{t("create_group")}</div>
          </button>
          <button type="button" data-testid="btn-join-group" onClick={() => setMode("join")}
            className="bg-white border border-neutral-200 text-[#02371b] rounded-2xl p-4 flex flex-col items-start gap-3 active:scale-[0.98] transition-transform">
            <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center"><Key size={18} weight="bold" /></div>
            <div className="font-display text-sm leading-tight text-left">{t("join_group")}</div>
          </button>
        </div>
      )}

      {mode === "create" && (
        <div className="bg-white border border-neutral-200 rounded-2xl p-5 mb-6" data-testid="create-group-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg">{t("create_group")}</h2>
            <button type="button" onClick={() => setMode(null)} className="text-neutral-400"><X size={18} /></button>
          </div>

          <label className="block text-[10px] font-bold tracking-[0.2em] uppercase text-neutral-500 mb-2">{t("choose_tournament")}</label>
          <div className="space-y-2 mb-4">
            {tournaments.map((tr) => (
              <button
                key={tr.tournament_id}
                type="button"
                data-testid={`select-tournament-${tr.tournament_id}`}
                onClick={() => setForm({ ...form, tournament_id: tr.tournament_id })}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors ${form.tournament_id === tr.tournament_id ? "border-[#02371b] bg-neutral-50" : "border-neutral-200 bg-white hover:border-neutral-300"}`}
              >
                <div className="w-9 h-9 rounded-full bg-[#41a42b]/10 text-[#41a42b] flex items-center justify-center overflow-hidden border border-neutral-200">
                  {tr.crest_url ? (
                    <img
                      src={tr.crest_url}
                      alt=""
                      className="w-full h-full object-contain p-0.5"
                    />
                  ) : (
                    <Trophy size={16} weight="fill" />
                  )}
                </div>
                <div className="flex-1 text-left">
                  <div className="font-bold text-sm text-[#02371b] flex items-center gap-2">
                    {tr.name}
                    {tr.popular && <span className="text-[9px] font-bold tracking-[0.15em] uppercase bg-[#FFD166] text-[#02371b] rounded-full px-2 py-0.5">{t("most_popular")}</span>}
                  </div>
                  <div className="text-[11px] text-neutral-500">{tr.country}</div>
                </div>
                {form.tournament_id === tr.tournament_id && <div className="w-2 h-2 rounded-full bg-[#41a42b]" />}
              </button>
            ))}
          </div>

          <label className="block text-[10px] font-bold tracking-[0.2em] uppercase text-neutral-500 mb-2">{t("group_name")}</label>
          <input data-testid="input-group-name"
            className="w-full border border-neutral-200 rounded-xl px-4 py-3 font-medium focus:outline-none focus:border-[#02371b]"
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("placeholder_champions_room")} />

          <label className="block text-[10px] font-bold tracking-[0.2em] uppercase text-neutral-500 mb-2 mt-4">{t("group_message")}</label>
          <p className="text-[11px] text-neutral-400 mb-2">{t("group_message_hint")}</p>
          <textarea data-testid="input-group-message" rows={3}
            className="w-full border border-neutral-200 rounded-xl px-4 py-3 font-medium focus:outline-none focus:border-[#02371b]"
            value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />

          <label className="block text-[10px] font-bold tracking-[0.2em] uppercase text-neutral-500 mb-2 mt-4">{t("scoring_system")}</label>
          <div className="grid grid-cols-2 gap-3 mb-2">
            <button
              type="button"
              onClick={() => setForm({ ...form, scoring_system: "classic" })}
              className={`p-3 rounded-xl border text-left transition-colors ${form.scoring_system === "classic" ? "border-[#02371b] bg-neutral-50" : "border-neutral-200 bg-white"}`}
            >
              <div className="font-bold text-sm text-[#02371b]">{t("scoring_system_classic_title")}</div>
              <div className="text-[10px] text-neutral-500 mt-1">{t("scoring_system_classic_desc")}</div>
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, scoring_system: "four_point" })}
              className={`p-3 rounded-xl border text-left transition-colors ${form.scoring_system === "four_point" ? "border-[#02371b] bg-neutral-50" : "border-neutral-200 bg-white"}`}
            >
              <div className="font-bold text-sm text-[#02371b]">{t("scoring_system_four_point_title")}</div>
              <div className="text-[10px] text-neutral-500 mt-1">{t("scoring_system_four_point_desc")}</div>
            </button>
          </div>

          <div className="mt-4 bg-neutral-50 border border-neutral-200 rounded-xl p-3 flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-[#02371b] flex items-center justify-center flex-shrink-0 mt-0.5">
              <div className="w-1.5 h-1.5 rounded-full bg-white" />
            </div>
            <div>
              <div className="font-bold text-sm">{t("private_group")}</div>
              <div className="text-[11px] text-neutral-500">{t("private_group_hint")}</div>
            </div>
          </div>
          {error && <div className="text-destructive text-xs mt-3">{error}</div>}
          <div className="flex gap-2 mt-5">
            <button type="button" onClick={() => setMode(null)} className="flex-1 border border-neutral-200 rounded-full py-3 text-sm font-bold text-neutral-600">{t("cancel")}</button>
            <button type="button" data-testid="submit-create-group" onClick={create}
              disabled={!form.name || !form.tournament_id || loading}
              className="flex-1 bg-[#41a42b] text-white rounded-full py-3 text-sm font-bold disabled:opacity-50 active:scale-95 transition-all">
              {t("save")}
            </button>
          </div>
        </div>
      )}

      {mode === "join" && (
        <div className="bg-white border border-neutral-200 rounded-2xl p-5 mb-6" data-testid="join-group-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg">{t("join_group")}</h2>
            <button type="button" onClick={() => setMode(null)} className="text-neutral-400"><X size={18} /></button>
          </div>
          <label className="block text-[10px] font-bold tracking-[0.2em] uppercase text-neutral-500 mb-2">{t("invite_code")}</label>
          <input data-testid="input-invite-code"
            className="w-full border border-neutral-200 rounded-xl px-4 py-3 font-mono-tab font-bold text-xl tracking-[0.3em] uppercase focus:outline-none focus:border-[#02371b]"
            value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            placeholder="ABC123" maxLength={8} />
          {error && <div className="text-destructive text-xs mt-3">{error}</div>}
          <div className="flex gap-2 mt-5">
            <button type="button" onClick={() => setMode(null)} className="flex-1 border border-neutral-200 rounded-full py-3 text-sm font-bold text-neutral-600">{t("cancel")}</button>
            <button type="button" data-testid="submit-join-group" onClick={join}
              disabled={!form.code || loading}
              className="flex-1 bg-[#02371b] text-white rounded-full py-3 text-sm font-bold disabled:opacity-50 active:scale-95 transition-all">
              {t("join")}
            </button>
          </div>
        </div>
      )}

      {initialLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="w-full flex items-center gap-3 p-4 bg-white border border-neutral-200 rounded-2xl animate-pulse">
              <div className="w-10 h-10 rounded-full bg-neutral-200 flex-shrink-0" />
              <div className="flex-1 min-w-0 space-y-2">
                <div className="h-4 bg-neutral-200 rounded-md w-3/4" />
                <div className="h-3 bg-neutral-200 rounded-md w-1/2" />
              </div>
              <div className="w-5 h-5 bg-neutral-200 rounded-full" />
            </div>
          ))}
        </div>
      ) : groups.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-2xl p-8 text-center">
          <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-yellow-400 to-amber-600 flex items-center justify-center shadow-lg">
            <Trophy size={56} weight="fill" className="text-white" />
          </div>
          <h3 className="font-display text-lg text-[#02371b]">{t("no_groups_title")}</h3>
          <p className="text-sm text-neutral-500 mt-2">{t("no_groups_sub")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => (
            <button key={g.group_id} type="button" data-testid={`group-item-${g.group_id}`}
              onClick={() => navigate(`/app/groups/${g.group_id}`)}
              className="w-full flex items-center gap-3 p-4 bg-white border border-neutral-200 rounded-2xl hover:border-[#02371b] transition-colors text-left">
              <div className="w-10 h-10 rounded-full bg-[#41a42b]/12 text-[#084d25] flex items-center justify-center flex-shrink-0">
                <UsersThree size={20} weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-[#02371b] truncate">{g.name}</div>
                <div className="text-[11px] text-neutral-500 flex items-center gap-1 mt-0.5">
                  <Users size={11} weight="bold" /> {g.member_count} {t("members")} · <span className="font-mono-tab">{g.invite_code}</span>
                </div>
                {g.tournament && (
                  <div className="text-[10px] text-neutral-400 mt-0.5 truncate">{g.tournament.name}</div>
                )}
              </div>
              <ArrowRight size={18} className="text-neutral-400" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
